import { get, isArray, isUndefined, some } from 'lodash';

import type { Currency } from '@swf/core/enums';

import type {
  AccumulatedLossLimitType,
  BaseGameLimits,
  DepositLimitValues,
  LimitCriteria,
  LimitFormValue,
  LimitResponse,
  SpendLimitType
} from './definitions';
import { SelfExclusionErrorCode } from './definitions';

export const LIMIT_UNSET = -1;

type LimitTypes = { limitType: string; type: number }[];

export const isInvalidPassword = (limitResponses: LimitResponse | LimitResponse[]) => {
  if (isArray(limitResponses)) {
    return some(limitResponses, { messages: { required: { pswdMatchRequired: '' } } });
  }

  return get(limitResponses, 'messages.required.pswdMatchRequired') === '';
};

export const selfExcludedUser = (response: LimitResponse) =>
  [SelfExclusionErrorCode.PERMANENT_SELF_EXCLUSION, SelfExclusionErrorCode.SELF_EXCLUSION].includes(
    response?.messages?.msg?.errcode as SelfExclusionErrorCode
  );

export const isInvalidSocialSecurityNumber = (limitResponses: LimitResponse) =>
  get(limitResponses, 'messages.msg.error') === 'SSN4_WRONG';

export const convertLimitsToDecimalValues = (gameLimits: BaseGameLimits, limitTypes: LimitTypes) =>
  Object.keys(gameLimits).map((item) => {
    const amount = gameLimits[item];
    return {
      type: limitTypes.find(({ limitType }) => limitType === item)?.type,
      amount: amount < 0 ? amount : amount * 100
    };
  });

export const convertLimitsToIntValues = (limits: LimitCriteria[], limitTypes: LimitTypes) =>
  limits.reduce((memo, { type, amount }) => {
    const limitName = limitTypes.find((limitType) => limitType.type === type)?.limitType;
    if (limitName) {
      memo[limitName] = amount < 0 ? amount : amount / 100;
    }
    return memo;
  }, {});

export const getMinutes = (limit: LimitFormValue | undefined): number => {
  if (!limit) {
    return 0;
  }

  const days = Number(limit.days) || 0;
  const hours = Number(limit.hours) || 0;
  const minutes = Number(limit.minutes) || 0;

  return days * 24 * 60 + hours * 60 + minutes;
};

export const hasNoLimitsChange = ({
  dailyLimit,
  weeklyLimit,
  monthlyLimit,
  previousDailyLimit,
  previousWeeklyLimit,
  previousMonthlyLimit
}: DepositLimitValues): boolean =>
  (isUndefined(dailyLimit) || dailyLimit * 100 === previousDailyLimit) &&
  (isUndefined(weeklyLimit) || weeklyLimit * 100 === previousWeeklyLimit) &&
  (isUndefined(monthlyLimit) || monthlyLimit * 100 === previousMonthlyLimit);

export const getLimitAmountByType = (
  limits: LimitCriteria[],
  limitType: SpendLimitType | AccumulatedLossLimitType
): number =>
  get(
    limits.find(({ type }) => type === limitType),
    'amount',
    LIMIT_UNSET
  );

export const getChangedLimit = (
  type: SpendLimitType | AccumulatedLossLimitType,
  currency: Currency,
  previousLimit?: number,
  currentLimit?: number,
  removeLimit = false
): LimitCriteria | undefined => {
  if (removeLimit) {
    return { amount: LIMIT_UNSET, currency, type };
  }
  const amount = Number(currentLimit) * 100;
  if (!isUndefined(currentLimit) && previousLimit !== amount) {
    return { amount, currency, type };
  }

  return;
};
